import { RotateCw } from "lucide-react";
import * as React from "react";

import { MediaKind } from "@/api/types";
import { cn } from "@/lib/utils";

import { HeaderCard } from "../booking/public/PublicChrome";
import {
  EXPORT_SIZE,
  FrameMode,
  QuarterTurns,
  Size,
  Transform,
  clampOffset,
  frameAspect,
  initialTransform,
  maxScale,
  minScale,
  modeOf,
  orientedSize,
  rescale,
  scaleFloor,
  suggestMode,
  turnClockwise,
} from "./cropGeometry";
import { decode, exportCrop, exportSource } from "./imageExport";

/**
 * Position a picture inside the frame it will actually appear in.
 *
 * <p>The mask is the point. For an avatar it is a circle the same shape as the rendered one, so what
 * the host lines up is literally what an invitee sees — no centre-and-hope, no cropped-off head. The
 * image is exported as the circle's bounding SQUARE though, not as a circle: the corners are kept, so
 * nothing is destroyed and a future rounded-square treatment still has pixels to use.</p>
 *
 * <h2>Fill and fit</h2>
 * <p>Cover-only framing is right for a photo and wrong for a logo. A banner is 3:1 and almost no
 * image a host owns is, so demanding coverage means taking a thin horizontal slice out of the middle
 * of a wordmark with no way to see the rest. {@code Fit whole image} drops the zoom floor to the
 * scale where the whole picture is inside the frame, and what is left over is left <b>transparent</b>
 * so the invitee page's own card colour shows through it.</p>
 *
 * <p>That transparency is the reason the margins are not a colour chosen here. A white baked into the
 * file would be white at midnight too. Leaving it empty means the same upload reads correctly in
 * light and dark, and keeps the decision with the page rather than with the uploader.</p>
 *
 * <p>Between the two floors the zoom is continuous, so a host is never forced to choose a mode: the
 * buttons are the two anchors worth having, and the slider covers everything between them.</p>
 *
 * <h2>Dragging without re-rendering</h2>
 * <p>A pointer emits moves faster than React can usefully re-render, and a 12MP photo makes each one
 * cost something. So a gesture writes straight to the element's own style inside an animation frame,
 * and the committed {@link Transform} is caught up once the host lets go. React state stays the
 * source of truth between gestures; during one, the DOM is ahead of it on purpose.</p>
 *
 * <p>The consequence worth knowing is that a re-render mid-gesture — a preview settling, say — would
 * paint from the stale committed transform and snap the picture back under the host's finger. A
 * layout effect re-applies the live placement after every render to prevent exactly that.</p>
 */

/** On-screen width of the framing stage, before the viewport gets a say. */
const STAGE_MAX = 480;
const STAGE_MIN = 240;

/** Width of the "as invitees see it" card. Narrower than the stage, because it is a reference. */
const PREVIEW_WIDTH = 320;

/** How long the picture must sit still before the preview re-renders. */
const PREVIEW_SETTLE_MS = 140;

export function ImageCropper({
  file,
  kind,
  hostName,
  otherAvatarUrl,
  otherBannerUrl,
  onCancel,
  onCropped,
}: {
  file: File;
  kind: MediaKind;
  /** Shown in the preview, so the header reads as the host's own rather than as a sample. */
  hostName: string;
  /** The host's saved avatar, when this cropper is framing a banner. */
  otherAvatarUrl?: string | null;
  /** The host's saved banner, when this cropper is framing an avatar. */
  otherBannerUrl?: string | null;
  onCancel: () => void;
  /** The framed image, and a bounded copy of the original kept so it can be reframed later. */
  onCropped: (cropped: File, source: File) => void;
}) {
  const [bitmap, setBitmap] = React.useState<ImageBitmap | null>(null);
  const [preview, setPreview] = React.useState<string | null>(null);
  /** Set when the browser cannot display the picked file, which is not the same as cannot read it. */
  const [undisplayable, setUndisplayable] = React.useState(false);
  const [transform, setTransform] = React.useState<Transform | null>(null);
  const [turns, setTurns] = React.useState<QuarterTurns>(0);
  const [error, setError] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  /** Whether the thirds guides are showing, which is exactly while a gesture is in progress. */
  const [guiding, setGuiding] = React.useState(false);

  const round = kind === "AVATAR";
  const aspect = frameAspect(kind);

  // The stage is measured rather than fixed, because a 3:1 banner framed in 320px is 107px tall and
  // nothing can be positioned precisely in it. Measured also means it survives a narrow phone.
  const shellRef = React.useRef<HTMLDivElement>(null);
  const [stageWidth, setStageWidth] = React.useState(STAGE_MAX);
  React.useLayoutEffect(() => {
    function measure() {
      const available = shellRef.current?.clientWidth ?? STAGE_MAX;
      setStageWidth(Math.max(STAGE_MIN, Math.min(STAGE_MAX, Math.floor(available))));
    }
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  const frame = React.useMemo(
    () => ({ width: stageWidth, height: Math.round(stageWidth / aspect) }),
    [stageWidth, aspect],
  );

  // Decode once, keyed on the FILE alone. Keying it on the frame as well would throw away the host's
  // framing every time the window was resized, and re-decode a 12MP photo to do it.
  React.useEffect(() => {
    let cancelled = false;
    let bmp: ImageBitmap | null = null;
    const url = URL.createObjectURL(file);
    setPreview(url);
    // A new file is a new decision about which way up it goes, and a new question about whether the
    // browser can show it.
    setTurns(0);
    setUndisplayable(false);

    decode(file)
      .then((decoded) => {
        bmp = decoded;
        if (cancelled) {
          decoded.close();
          return;
        }
        setBitmap(decoded);
      })
      .catch(() => {
        if (!cancelled) setError("That image could not be read.");
      });

    return () => {
      cancelled = true;
      URL.revokeObjectURL(url);
      // A decoded 12MP photo is tens of megabytes of pixels until this runs.
      bmp?.close();
    };
  }, [file]);

  /**
   * A stage image for a file the browser can decode but cannot display.
   *
   * <p>Exactly the HEIC case, and a trap worth naming: the wasm decoder hands back a perfectly good
   * bitmap, so framing, preview and export all work — while the stage {@code <img>}, which points at
   * the picked file itself, quietly shows nothing. Every control would be live over a blank square.
   * So the decoded pixels are written back out in a format an {@code <img>} will take.</p>
   *
   * <p>Driven by the element failing rather than by guessing at formats, because "can this browser
   * display this file" has no reliable answer short of asking it. Costs an encode only for the
   * pictures that need one.</p>
   */
  React.useEffect(() => {
    if (!undisplayable || !bitmap) return;
    let cancelled = false;
    let url: string | null = null;

    // The same bounded copy the reframe-later original uses: at most 2048px against a stage under
    // 480, so there is nothing on screen to tell it from the file itself. The EXPORT still draws
    // from the full bitmap.
    exportSource(bitmap, "preview")
      .then((shown) => {
        if (cancelled) return;
        url = URL.createObjectURL(shown);
        setPreview(url);
      })
      .catch(() => setError("That image could not be shown."));

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [undisplayable, bitmap]);

  // The bitmap's own dimensions, used for DRAWING, and its footprint after rotation, used for every
  // decision about how it meets the frame. Keeping the two apart is what confines rotation to one
  // idea rather than a term inside all the geometry.
  const imageSize = React.useMemo(
    () => (bitmap ? { width: bitmap.width, height: bitmap.height } : null),
    [bitmap],
  );
  const oriented = React.useMemo(
    () => (imageSize ? orientedSize(imageSize, turns) : null),
    [imageSize, turns],
  );

  // Seed the framing once the image is known, and carry it across a resize afterwards. Both live
  // here because both answer the same question: what does this transform mean at THIS stage width?
  //
  // Keyed on the BITMAP, not on having run before. Today the cropper unmounts between pictures, so a
  // plain "first time" flag would be equivalent — but only by luck of where it is mounted, and the
  // failure if that changed is a new image silently inheriting the last one's framing.
  const framed = React.useRef<{ bitmap: ImageBitmap; width: number } | null>(null);
  React.useEffect(() => {
    if (!bitmap || !oriented) return;
    if (framed.current?.bitmap !== bitmap) {
      // The opening mode is chosen from the picture, not fixed: a wide photo fills a banner and looks
      // intended, while a square logo in the same frame loses two thirds of itself. An avatar frame
      // is square and a face belongs in it, so that one always starts filled.
      setTransform(initialTransform(oriented, frame, round ? "FILL" : suggestMode(oriented, frame)));
    } else if (framed.current.width !== frame.width) {
      const from = framed.current.width;
      setTransform((t) => (t ? clampOffset(rescale(t, from, frame.width), oriented, frame) : t));
    }
    framed.current = { bitmap, width: frame.width };
  }, [bitmap, oriented, frame, round]);

  const lo = oriented ? scaleFloor("FIT", oriented, frame) : 1;
  const cover = oriented ? minScale(oriented, frame) : 1;
  const hi = oriented ? maxScale(oriented, frame, EXPORT_SIZE[kind]) : 1;
  const mode: FrameMode = oriented && transform ? modeOf(transform, oriented, frame) : "FILL";

  /** Zoom a transform about the frame's centre, so the point the host lined up stays where it is. */
  const zoomed = React.useCallback(
    (base: Transform, scale: number): Transform | null => {
      if (!oriented) return null;
      const clamped = Math.min(hi, Math.max(lo, scale));
      const ratio = clamped / base.scale;
      return clampOffset({ scale: clamped, dx: base.dx * ratio, dy: base.dy * ratio }, oriented, frame);
    },
    [oriented, frame, lo, hi],
  );

  const zoomTo = React.useCallback(
    (scale: number) => setTransform((t) => (t ? zoomed(t, scale) ?? t : t)),
    [zoomed],
  );

  /** Jump to a mode's anchor scale, centred. The two presets the slider sits between. */
  function chooseMode(next: FrameMode) {
    if (!oriented) return;
    setTransform(initialTransform(oriented, frame, next));
  }

  /**
   * Turn the picture a quarter turn, and re-frame it.
   *
   * <p>Re-seeded rather than carried, because after a turn the old framing describes a shape that no
   * longer exists — a scale that fitted a landscape photo leaves a portrait one hanging out of the
   * frame. The MODE is preserved, which is the part the host actually chose; where they had dragged
   * it to is not, because there is no honest answer to where that point went.</p>
   */
  function rotate() {
    if (!imageSize) return;
    const next = turnClockwise(turns);
    setTurns(next);
    setTransform(initialTransform(orientedSize(imageSize, next), frame, mode));
  }

  // Pointer events rather than mouse/touch pairs: one code path covers mouse, pen and finger, and
  // setPointerCapture keeps the drag alive when the pointer leaves the stage mid-gesture.
  const pointers = React.useRef(new Map<number, { x: number; y: number }>());
  const dragRef = React.useRef<{ x: number; y: number; from: Transform } | null>(null);
  const pinchRef = React.useRef<{ distance: number; scale: number } | null>(null);

  // The live placement, which during a gesture is AHEAD of `transform`. Everything a gesture reads
  // comes from here rather than from state, or each move would compute from a value React has not
  // caught up to yet.
  const liveRef = React.useRef<Transform | null>(null);
  const gesturing = React.useRef(false);
  const stageRef = React.useRef<HTMLDivElement>(null);
  const stageImgRef = React.useRef<HTMLImageElement>(null);
  const frameRequest = React.useRef(0);

  React.useEffect(() => {
    if (!gesturing.current) liveRef.current = transform;
  }, [transform]);

  /** Put the live placement on screen at the next frame, coalescing however many moves arrived. */
  const paint = React.useCallback(() => {
    if (frameRequest.current) return;
    frameRequest.current = window.requestAnimationFrame(() => {
      frameRequest.current = 0;
      const el = stageImgRef.current;
      if (!el || !liveRef.current || !imageSize) return;
      const p = placement(liveRef.current, imageSize, turns);
      el.style.width = `${p.width}px`;
      el.style.height = `${p.height}px`;
      el.style.transform = p.transform;
    });
  }, [imageSize, turns]);

  /** Move the picture now, on the DOM. React finds out when the gesture ends. */
  const applyLive = React.useCallback(
    (next: Transform | null) => {
      if (!next || !oriented) return;
      liveRef.current = clampOffset(next, oriented, frame);
      paint();
    },
    [oriented, frame, paint],
  );

  // A render mid-gesture would rewrite the style from the committed transform and snap the picture
  // back under the host's finger. No dependency array: this has to run after EVERY render, since the
  // whole point is the renders we did not cause.
  React.useLayoutEffect(() => {
    if (gesturing.current) paint();
  });

  React.useEffect(() => () => window.cancelAnimationFrame(frameRequest.current), []);

  function spread() {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  }

  function onPointerDown(e: React.PointerEvent) {
    const from = liveRef.current;
    if (!from) return;
    (e.target as Element).setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    gesturing.current = true;
    setGuiding(true);
    if (pointers.current.size === 2) {
      // A pinch starts where the drag stops, so the picture does not lurch as the second finger lands.
      dragRef.current = null;
      pinchRef.current = { distance: spread(), scale: from.scale };
    } else {
      dragRef.current = { x: e.clientX, y: e.clientY, from };
    }
  }

  function onPointerMove(e: React.PointerEvent) {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

    const pinch = pinchRef.current;
    if (pinch && pointers.current.size === 2) {
      if (pinch.distance > 0 && liveRef.current) {
        applyLive(zoomed(liveRef.current, (pinch.scale * spread()) / pinch.distance));
      }
      return;
    }
    const drag = dragRef.current;
    if (!drag) return;
    applyLive({
      scale: drag.from.scale,
      dx: drag.from.dx + (e.clientX - drag.x),
      dy: drag.from.dy + (e.clientY - drag.y),
    });
  }

  function onPointerUp(e: React.PointerEvent) {
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinchRef.current = null;

    // Lifting one finger of a pinch leaves the other holding the picture, rather than dropping it.
    const [remaining] = [...pointers.current.values()];
    dragRef.current =
      remaining && liveRef.current ? { x: remaining.x, y: remaining.y, from: liveRef.current } : null;

    if (pointers.current.size > 0) return;
    // Last finger up: hand the result back to React, which also lets the preview settle on it.
    gesturing.current = false;
    setGuiding(false);
    setTransform(liveRef.current);
  }

  /**
   * Wheel zoom, on a listener we attach ourselves.
   *
   * <p>React registers {@code onWheel} as a PASSIVE listener, where {@code preventDefault} is
   * defined to do nothing. Left as a React prop this reads like working code and is not: the picture
   * zooms and the dialog scrolls out from under it at the same time. A native listener registered
   * {@code passive: false} is the only way to have the one without the other.</p>
   */
  React.useEffect(() => {
    const el = stageRef.current;
    if (!el) return;

    function onWheel(e: WheelEvent) {
      const base = liveRef.current;
      if (!base) return;
      e.preventDefault();
      const next = zoomed(base, base.scale * (e.deltaY < 0 ? 1.08 : 1 / 1.08));
      if (!next) return;
      // Wheeling has no "let go", so it commits at once — unless a gesture is already running, in
      // which case that gesture owns the picture and will commit for both.
      liveRef.current = next;
      if (gesturing.current) paint();
      else setTransform(next);
    }

    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
  }, [zoomed, paint]);

  // Nudge with the keyboard, so framing is not mouse-only.
  function onKeyDown(e: React.KeyboardEvent) {
    if (!transform || !oriented) return;
    const step = e.shiftKey ? 20 : 4;
    const by = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] }[
      e.key
    ];
    if (!by) return;
    e.preventDefault();
    setTransform(
      clampOffset({ scale: transform.scale, dx: transform.dx + by[0], dy: transform.dy + by[1] }, oriented, frame),
    );
  }

  async function save() {
    if (!bitmap || !transform) return;
    setBusy(true);
    setError(null);
    try {
      const cropped = await exportCrop(bitmap, transform, frame, kind, file.name, turns);
      const source = await exportSource(bitmap, file.name, turns);
      onCropped(cropped, source);
    } catch {
      setError("That image could not be prepared. Try another.");
      setBusy(false);
    }
  }

  // The preview renders the REAL invitee header, fed a snapshot of the current framing. Rebuilding
  // the header's markup here would be a fourth copy of it, and PublicChrome exists because the third
  // was already one too many.
  const snapshot = useCropSnapshot(bitmap, transform, frame, kind, turns);

  return (
    <div
      // Literal black, not foreground/60: a scrim has to darken in BOTH themes, and
      // --foreground inverts to near-white in dark. The Dialog primitive does the same.
      className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Position your picture"
    >
      <div className="my-auto w-full max-w-xl space-y-4 rounded-2xl border border-border bg-card p-5 shadow-lg">
        <div className="font-comic-headline text-sm uppercase tracking-wide">
          Position your picture
        </div>

        <div ref={shellRef} className="flex justify-center">
          <div
            ref={stageRef}
            tabIndex={0}
            onKeyDown={onKeyDown}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            style={{ width: frame.width, height: frame.height }}
            className={cn(
              // bg-card, not a neutral: in Fit the margins are exactly what an invitee will see
              // behind the picture, so the stage has to be that colour rather than a stand-in.
              "relative touch-none overflow-hidden border border-border bg-card outline-none",
              "cursor-grab active:cursor-grabbing",
              "ring-offset-2 focus-visible:ring-2 focus-visible:ring-brand-blue",
              round ? "rounded-full" : "rounded-lg",
            )}
          >
            {preview && transform && imageSize && (
              <img
                ref={stageImgRef}
                src={preview}
                alt=""
                draggable={false}
                onError={() => setUndisplayable(true)}
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  // The <img> shows the ORIGINAL file, so the browser applies EXIF orientation on
                  // its own. The bitmap we export from was decoded with the same orientation
                  // applied, which is what keeps the preview honest.
                  maxWidth: "none",
                  ...placement(transform, imageSize, turns),
                }}
              />
            )}
            {guiding && <ThirdsGuides />}
            {!bitmap && !error && (
              <div className="grid h-full place-items-center text-xs text-muted-foreground">
                Loading…
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          <ModeButton active={mode === "FILL"} disabled={!bitmap} onClick={() => chooseMode("FILL")}>
            Fill frame
          </ModeButton>
          <ModeButton
            active={mode === "FIT"}
            // Nothing to offer when the image already has the frame's ratio: both modes are the same
            // picture, and a button that visibly does nothing is worse than one that is plainly off.
            disabled={!bitmap || lo >= cover - 1e-6}
            onClick={() => chooseMode("FIT")}
          >
            Fit whole image
          </ModeButton>
          <button
            type="button"
            disabled={!bitmap}
            onClick={rotate}
            aria-label="Rotate a quarter turn"
            className="grid h-8 w-8 place-items-center rounded-lg border border-border hover:bg-muted/50 disabled:opacity-50"
          >
            <RotateCw className="h-4 w-4" aria-hidden />
          </button>
        </div>

        <div className="flex items-center gap-2 text-sm">
          <span className="w-10 shrink-0 text-muted-foreground">Zoom</span>
          <StepButton label="Zoom out" disabled={!bitmap} onClick={() => zoomTo((transform?.scale ?? lo) / 1.15)}>
            &minus;
          </StepButton>
          <input
            type="range"
            min={lo}
            max={hi}
            step={(hi - lo) / 200 || 0.01}
            value={transform?.scale ?? lo}
            disabled={!bitmap}
            onChange={(e) => zoomTo(Number(e.target.value))}
            aria-label="Zoom"
            className="flex-1"
          />
          <StepButton label="Zoom in" disabled={!bitmap} onClick={() => zoomTo((transform?.scale ?? lo) * 1.15)}>
            +
          </StepButton>
          <button
            type="button"
            disabled={!bitmap}
            onClick={() => chooseMode(mode)}
            className="rounded-lg border border-border px-2.5 py-1 text-sm hover:bg-muted/50 disabled:opacity-50"
          >
            Reset
          </button>
        </div>

        <div className="space-y-2">
          <div className="text-xs uppercase tracking-wide text-muted-foreground">
            As invitees see it
          </div>
          <div className="flex justify-center">
            <div style={{ width: PREVIEW_WIDTH }}>
              <HeaderCard
                name={hostName}
                subtitle="Book a time"
                avatarUrl={round ? snapshot : otherAvatarUrl}
                bannerUrl={round ? otherBannerUrl : snapshot}
              />
            </div>
          </div>
        </div>

        {error && <p className="text-xs text-destructive-strong">{error}</p>}

        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-lg border border-border px-3 py-1.5 text-sm hover:bg-muted/50"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={!bitmap || busy}
            onClick={save}
            className="rounded-lg bg-brand-blue px-3 py-1.5 text-sm font-semibold text-brand-blue-foreground hover:brightness-110 disabled:opacity-50"
          >
            {busy ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Where the image sits on the stage, as CSS.
 *
 * <p>The single place the on-screen placement is expressed, because it is written twice: by React on
 * a normal render, and straight onto the element during a gesture. Two spellings of the same
 * arithmetic would drift, and the symptom would be a picture that jumps the moment a host lets go.</p>
 *
 * <p>Order matters in the transform. {@code rotate} is applied first, about the element's own centre,
 * and the translate then places that centre — which is precisely the arrangement {@code destRect}
 * assumes, and why a rotated image lands where the geometry says it does.</p>
 */
function placement(t: Transform, image: Size, turns: QuarterTurns) {
  return {
    width: image.width * t.scale,
    height: image.height * t.scale,
    transform: `translate(calc(-50% + ${t.dx}px), calc(-50% + ${t.dy}px)) rotate(${turns * 90}deg)`,
  };
}

/**
 * Rule-of-thirds guides, shown only while the picture is being moved.
 *
 * <p>Only while moving, because that is the moment they help and every other moment they are clutter
 * over the thing the host is trying to judge. Purely decorative, so the whole overlay is hidden from
 * assistive technology rather than announced as four anonymous boxes.</p>
 */
function ThirdsGuides() {
  return (
    <div className="pointer-events-none absolute inset-0" aria-hidden>
      {[1, 2].map((n) => (
        <div
          key={`v${n}`}
          // mix-blend-difference needs a literal white to invert against the photo;
          // a token would make the crop guides vanish on some images.
          className="absolute inset-y-0 w-px bg-white/40 mix-blend-difference"
          style={{ left: `${(n * 100) / 3}%` }}
        />
      ))}
      {[1, 2].map((n) => (
        <div
          key={`h${n}`}
          className="absolute inset-x-0 h-px bg-white/40 mix-blend-difference"
          style={{ top: `${(n * 100) / 3}%` }}
        />
      ))}
    </div>
  );
}

function ModeButton({
  active,
  disabled,
  onClick,
  children,
}: {
  active: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "rounded-lg border px-3 py-1 text-sm transition disabled:opacity-50",
        active
          ? "border-brand-blue bg-brand-blue/10 font-semibold text-brand-blue"
          : "border-border hover:bg-muted/50",
      )}
    >
      {children}
    </button>
  );
}

function StepButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="grid h-7 w-7 shrink-0 place-items-center rounded-lg border border-border text-sm hover:bg-muted/50 disabled:opacity-50"
    >
      {children}
    </button>
  );
}

/**
 * A data URL of the current framing, refreshed once the host stops moving.
 *
 * <p>Debounced rather than live: this is a real encode of a 1536x512 canvas, and running it on every
 * pointer move would fight the drag it is meant to illustrate. Settling instead means the preview is
 * always a little behind the stage and always exactly right, which is the useful way round.</p>
 */
function useCropSnapshot(
  bitmap: ImageBitmap | null,
  transform: Transform | null,
  frame: { width: number; height: number },
  kind: MediaKind,
  turns: QuarterTurns,
): string | null {
  const [url, setUrl] = React.useState<string | null>(null);

  // Mirrored in a ref purely so unmount can revoke the last one. Reading it out of state at cleanup
  // time would mean a setState during teardown, which is a no-op that happens to work rather than a
  // thing to rely on.
  const live = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!bitmap || !transform) return;
    let cancelled = false;
    const timer = window.setTimeout(() => {
      exportCrop(bitmap, transform, frame, kind, "preview", turns)
        .then((snap) => {
          if (cancelled) return;
          // Revoked only once its replacement exists, or the <img> still showing it would break.
          const previous = live.current;
          live.current = URL.createObjectURL(snap);
          setUrl(live.current);
          if (previous) URL.revokeObjectURL(previous);
        })
        .catch(() => {
          /* The preview is a courtesy; the stage above it is the source of truth. */
        });
    }, PREVIEW_SETTLE_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [bitmap, transform, frame, kind, turns]);

  React.useEffect(
    () => () => {
      if (live.current) URL.revokeObjectURL(live.current);
    },
    [],
  );

  return url;
}
